import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Pencil, Printer, Receipt, Search, Users } from 'lucide-react';
import { fetchCustomerInvoices, fetchInvoice } from '../api/resources';
import { inr, fmtDate } from '../utils/format';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import { StatusBadge } from '../components/ui/Badge';
import MeasurementValue from '../components/ui/MeasurementValue';
import OrderTimeline from '../components/invoice/OrderTimeline';

/**
 * Everything one customer has ever ordered, on its own page so the order
 * screen stays clear. A row opens in place: dates, status, money and the
 * garments with their measurements — enough to answer "what did we make for
 * them last time?" without leaving the list.
 */
export default function CustomerOrders() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  // Coming back to a half-filled order asks for it to be put back; arriving
  // here from a menu just goes back the ordinary way.
  const goBack = () => {
    const from = location.state?.from;
    if (from) nav(from, { state: { ...(location.state?.wizardState || {}), resumeDraft: true } });
    else nav(-1);
  };
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customer-invoices', id],
    queryFn: () => fetchCustomerInvoices(id),
  });
  const invoices = data?.invoices || [];
  const customer = data?.customer;

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return invoices;
    return invoices.filter((i) => [i.orderNo, i.date, i.itemsText, i.status, i.reference]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [invoices, q]);

  const money = invoices.reduce((a, i) => ({
    total: a.total + Number(i.totalAmount || 0),
    due: a.due + Number(i.dueAmount || 0),
  }), { total: 0, due: 0 });

  if (isLoading) return <Spinner label="Loading orders…" />;

  return (
    <div>
      <PageHeader
        icon={Users}
        title={data?.customerName ? `${data.customerName} — Orders` : 'Customer Orders'}
        subtitle={[
          customer?.mobile,
          [customer?.city, customer?.state].filter(Boolean).join(', '),
          `${invoices.length} order${invoices.length === 1 ? '' : 's'}`,
          `Billed ${inr(money.total)}`,
          money.due > 0 ? `Due ${inr(money.due)}` : 'Nothing due',
        ].filter(Boolean).join(' · ')}
        actions={
          <>
            <button className="btn-outline" onClick={goBack}>
              <ArrowLeft size={15} /> Back
            </button>
            <button className="btn-primary" onClick={() => nav(`/invoices/new?customer=${id}`)}>
              <Receipt size={15} /> New order
            </button>
          </>
        }
      />

      <div className="card mod-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
          <div className="relative min-w-[12rem] flex-1 sm:max-w-xs">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input className="input input-sm pl-8" placeholder="Search order no., item, status…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <span className="text-2xs text-content-subtle">{rows.length} shown · click a row for details</span>
        </div>

        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm italic text-content-subtle">
            {invoices.length ? 'No order matches that search.' : 'This customer has no orders yet.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="th w-8" />
                  <th className="th">Order No.</th>
                  <th className="th">Date</th>
                  <th className="th">Delivery</th>
                  <th className="th">Items</th>
                  <th className="th text-right">Bill</th>
                  <th className="th text-right">Received</th>
                  <th className="th text-right">Due</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => {
                  const open = openId === i.id;
                  return (
                    <>
                      <tr
                        key={i.id}
                        className={`cursor-pointer border-t border-line ${open ? 'bg-brand-50' : 'hover:bg-surface-hover'}`}
                        onClick={() => setOpenId(open ? null : i.id)}
                      >
                        <td className="px-2 py-1.5 text-content-subtle">
                          <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
                        </td>
                        <td className="px-3 py-1.5 font-semibold text-content">{i.orderNo}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{i.date}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-content-muted">{i.delivery || '—'}</td>
                        <td className="max-w-[18rem] truncate px-3 py-1.5 text-content-muted" title={i.itemsText}>{i.itemsText || '—'}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{inr(i.totalAmount)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-success-600">{inr(i.receivedAmount)}</td>
                        <td className={`px-3 py-1.5 text-right tabular-nums ${i.dueAmount > 0 ? 'font-semibold text-danger-600' : ''}`}>{inr(i.dueAmount)}</td>
                        <td className="px-3 py-1.5"><StatusBadge status={i.status} /></td>
                      </tr>
                      {open && (
                        <tr key={`${i.id}-d`} className="border-t border-line bg-surface-sunken">
                          <td />
                          <td colSpan={8} className="px-3 pb-3 pt-1">
                            <OrderDetail invoiceId={i.id} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// The compact box under an opened row.
function OrderDetail({ invoiceId }) {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['invoice', String(invoiceId)],
    queryFn: () => fetchInvoice(invoiceId),
  });
  if (isLoading) return <div className="py-3"><Spinner /></div>;
  const inv = data?.invoice;
  if (!inv) return null;

  const fact = (k, v) => (
    <div className="min-w-[8rem]">
      <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">{k}</p>
      <p className="text-sm font-medium text-content">{v || '—'}</p>
    </div>
  );

  return (
    <div className="rounded-lg border border-line bg-surface p-3">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        {fact('Order date', fmtDate(inv.orderDate))}
        {fact('Delivery', inv.deliveryDate ? fmtDate(inv.deliveryDate) : '—')}
        {fact('Trial', inv.trialDate ? fmtDate(inv.trialDate) : '—')}
        {fact('Status', <StatusBadge status={inv.status} />)}
        {fact('Reference', inv.reference)}
        {fact('Bill', inr(inv.totalAmount))}
        {fact('Received', inr(inv.receivedAmount))}
        {fact('Due', <span className={inv.dueAmount > 0 ? 'text-danger-600' : ''}>{inr(inv.dueAmount)}</span>)}
        <div className="ml-auto flex gap-1.5">
          <button className="btn-outline btn-sm" onClick={() => nav(`/invoices/${inv.id}`)}><Printer size={13} /> Print</button>
          <button className="btn-primary btn-sm" onClick={() => nav(`/invoices/${inv.id}/edit`)}><Pencil size={13} /> Open order</button>
        </div>
      </div>

      <div className="mt-3 border-t border-line pt-3">
        <OrderTimeline invoiceId={invoiceId} compact />
      </div>

      <div className="mt-3 space-y-1.5">
        {(inv.items || []).map((it) => (
          <div key={it.id} className="rounded-md border border-line px-2.5 py-1.5">
            <p className="text-sm font-semibold text-content">
              {it.itemName} <span className="font-normal text-content-subtle">× {it.qty}</span>
              {it.alteration && <span className="ml-1 text-2xs font-semibold text-warning-700">Alteration</span>}
              <span className="float-right font-bold tabular-nums">{inr(it.finalAmt)}</span>
            </p>
            {(it.measurements || []).length > 0 && (
              <p className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-content-muted">
                {it.measurements.map((m) => (
                  <span key={m.measurementId} className="whitespace-nowrap">
                    {m.name} <MeasurementValue value={m.value} className="font-semibold text-content" />
                    {m.notes && <span className="text-content-subtle"> ({m.notes})</span>}
                  </span>
                ))}
              </p>
            )}
            {(it.assignments || []).length > 0 && (
              <p className="mt-0.5 text-2xs text-content-subtle">
                {it.assignments.map((a) => `${a.workType === 'CUTTING' ? 'Cutting' : 'Stitching'}: ${a.workerName || '—'} (${a.status.replace('_', ' ').toLowerCase()})`).join(' · ')}
              </p>
            )}
            {it.remarks && <p className="mt-0.5 text-xs italic text-content-muted">{it.remarks}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
